/**
 * Utility functions for managing PDF projects in IndexedDB
 */

import { storage } from './localStorage';
import { indexedDBStorage } from './indexedDBStorage';

export interface PDFField {
  id: string;
  name: string;
  type: 'text' | 'checkbox' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  pageIndex: number;
  isExisting?: boolean;
  fontSize?: number;
  checkboxSize?: number;
  imageData?: string;
  imageName?: string;
}

export interface SavedPDFProject {
  id: string;
  name: string;
  createdAt: number;
  lastModified: number;
  pdfData: string; // Base64 encoded PDF file
  originalFileName: string;
  fields: PDFField[];
  existingFields: PDFField[];
  currentPage: number;
  totalPages: number;
}

const PDF_PROJECTS_KEY = 'pdf-editor-projects';
const MIGRATION_KEY = 'pdf-projects-migrated';

/**
 * Migrate projects from localStorage to IndexedDB if needed
 */
const migrateFromLocalStorage = async (): Promise<void> => {
  try {
    // Check if migration has already been done
    const migrated = await indexedDBStorage.getItem(MIGRATION_KEY, false);
    if (migrated) {
      return;
    }

    // Check if IndexedDB is available
    if (!indexedDBStorage.isAvailable()) {
      console.warn('IndexedDB not available, falling back to localStorage');
      return;
    }

    // Get projects from localStorage
    const localStorageProjects = storage.get<SavedPDFProject[]>(PDF_PROJECTS_KEY, []);
    
    if (localStorageProjects.length > 0) {
      console.log(`Migrating ${localStorageProjects.length} projects from localStorage to IndexedDB`);
      
      // Save to IndexedDB
      await indexedDBStorage.setItem(PDF_PROJECTS_KEY, localStorageProjects);
      
      // Clear from localStorage to free up space
      storage.remove(PDF_PROJECTS_KEY);
      
      console.log('Migration completed successfully');
    }

    // Mark migration as complete
    await indexedDBStorage.setItem(MIGRATION_KEY, true);
  } catch (error) {
    console.error('Failed to migrate projects from localStorage to IndexedDB:', error);
  }
};

/**
 * Get all saved PDF projects
 */
export const getSavedProjects = async (): Promise<SavedPDFProject[]> => {
  try {
    // Ensure migration is done first
    await migrateFromLocalStorage();
    
    // Try to get from IndexedDB first
    if (indexedDBStorage.isAvailable()) {
      return await indexedDBStorage.getItem<SavedPDFProject[]>(PDF_PROJECTS_KEY, []);
    } else {
      // Fallback to localStorage
      return storage.get<SavedPDFProject[]>(PDF_PROJECTS_KEY, []);
    }
  } catch (error) {
    console.error('Failed to get saved projects:', error);
    // Fallback to localStorage on error
    return storage.get<SavedPDFProject[]>(PDF_PROJECTS_KEY, []);
  }
};

/**
 * Save a PDF project
 */
export const savePDFProject = async (project: Omit<SavedPDFProject, 'id' | 'createdAt' | 'lastModified'>): Promise<boolean> => {
  try {
    // Ensure migration is done first
    await migrateFromLocalStorage();
    
    const projects = await getSavedProjects();
    const now = Date.now();
    
    // Check if project with same name exists
    const existingIndex = projects.findIndex(p => p.name === project.name);
    
    const savedProject: SavedPDFProject = {
      ...project,
      id: existingIndex >= 0 ? projects[existingIndex].id : generateProjectId(),
      createdAt: existingIndex >= 0 ? projects[existingIndex].createdAt : now,
      lastModified: now,
    };
    
    if (existingIndex >= 0) {
      // Update existing project
      projects[existingIndex] = savedProject;
    } else {
      // Add new project
      projects.push(savedProject);
    }
    
    // Try to save to IndexedDB first
    if (indexedDBStorage.isAvailable()) {
      return await indexedDBStorage.setItem(PDF_PROJECTS_KEY, projects);
    } else {
      // Fallback to localStorage
      return storage.set(PDF_PROJECTS_KEY, projects);
    }
  } catch (error) {
    console.error('Failed to save PDF project:', error);
    return false;
  }
};

/**
 * Get a specific PDF project by ID
 */
export const getPDFProject = async (id: string): Promise<SavedPDFProject | null> => {
  const projects = await getSavedProjects();
  return projects.find(p => p.id === id) || null;
};

/**
 * Delete a PDF project
 */
export const deletePDFProject = async (id: string): Promise<boolean> => {
  try {
    const projects = await getSavedProjects();
    const filteredProjects = projects.filter(p => p.id !== id);
    
    // Try to save to IndexedDB first
    if (indexedDBStorage.isAvailable()) {
      return await indexedDBStorage.setItem(PDF_PROJECTS_KEY, filteredProjects);
    } else {
      // Fallback to localStorage
      return storage.set(PDF_PROJECTS_KEY, filteredProjects);
    }
  } catch (error) {
    console.error('Failed to delete PDF project:', error);
    return false;
  }
};

/**
 * Clear all PDF projects
 */
export const clearAllProjects = async (): Promise<boolean> => {
  try {
    // Try to clear from IndexedDB first
    if (indexedDBStorage.isAvailable()) {
      return await indexedDBStorage.setItem(PDF_PROJECTS_KEY, []);
    } else {
      // Fallback to localStorage
      return storage.set(PDF_PROJECTS_KEY, []);
    }
  } catch (error) {
    console.error('Failed to clear all PDF projects:', error);
    return false;
  }
};

/**
 * Check if project name already exists
 */
export const projectNameExists = async (name: string, excludeId?: string): Promise<boolean> => {
  const projects = await getSavedProjects();
  return projects.some(p => p.name === name && p.id !== excludeId);
};

/**
 * Generate a unique project ID
 */
const generateProjectId = (): string => {
  return `project_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get storage usage for PDF projects
 */
export const getProjectsStorageUsage = async (): Promise<{ size: number; count: number }> => {
  try {
    const projects = await getSavedProjects();
    const serialized = JSON.stringify(projects);
    return {
      size: new Blob([serialized]).size,
      count: projects.length,
    };
  } catch (error) {
    console.error('Failed to get storage usage:', error);
    return { size: 0, count: 0 };
  }
};

/**
 * Synchronous version of getProjectsStorageUsage for backward compatibility
 * This will only work with localStorage fallback
 */
export const getProjectsStorageUsageSync = (): { size: number; count: number } => {
  try {
    const projects = storage.get<SavedPDFProject[]>(PDF_PROJECTS_KEY, []);
    const serialized = JSON.stringify(projects);
    return {
      size: new Blob([serialized]).size,
      count: projects.length,
    };
  } catch (error) {
    console.error('Failed to get storage usage:', error);
    return { size: 0, count: 0 };
  }
};

/**
 * Convert File to base64 string
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix to store only the base64 data
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * Convert base64 string back to File
 */
export const base64ToFile = (base64: string, fileName: string): File => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], fileName, { type: 'application/pdf' });
};